import React, { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import InnerHero from "../../components/InnerHero";
import { getSportsAchievementBySlug } from "./SportsAchievement";
import { ArrowLeft } from "lucide-react";

const SPORTS_HERO_IMAGE = "/Campus/sportshero.jpg";

const SportsAchievementDetail = () => {
  const { slug } = useParams();
  const achievement = getSportsAchievementBySlug(slug);

  useEffect(() => {
    if (achievement) {
      document.title = `${achievement.title} | Achievement | KLS VDIT`;
      updateMeta("description", achievement.summary);
      updateMeta("og:title", achievement.title);
      updateMeta("og:description", achievement.summary);
      updateMeta("og:image", achievement.image);
    }
  }, [achievement]);

  if (!achievement) {
    return (
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-24">
        <p className="text-brand text-xl">Achievement not found.</p>
        <Link to="/campus-life/sports-gym/sports-achievement" className="inline-flex items-center gap-2 text-brand underline mt-3">
          <ArrowLeft size={14} /> Back to Sports Achievements
        </Link>
      </main>
    );
  }

  const hasParticipantAchievement = achievement.students.some((student) => student.length > 4);
  const participantHeadings = hasParticipantAchievement
    ? ["#", "Name", "Achievement"]
    : ["#", "Name"];

  return (
    <main>
      <InnerHero
        title={achievement.title}
        image={SPORTS_HERO_IMAGE}
        breadcrumbs={[
          { label: "Campus Life", to: "/campus-life/sports-gym/overview" },
          { label: "Sports Achievement", to: "/campus-life/sports-gym/sports-achievement" },
          { label: achievement.title },
        ]}
      />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-12">
        <div className="space-y-8">
          <article className="bg-white border border-brand/15 rounded-xl overflow-hidden shadow-sm">
            <div className="p-5 sm:p-7 md:p-8 space-y-6">
              <div className="space-y-3">
                <p className="text-xs sm:text-sm uppercase tracking-wider text-[#3a3a3a]/70 font-sans-ui">
                  {achievement.tournament}
                </p>
                <h2
                  className="text-2xl sm:text-3xl text-brand font-semibold leading-tight"
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {achievement.title}
                </h2>
                <p className="text-[#2a2a2a]/85 leading-relaxed text-[15.5px] sm:text-base">
                  {achievement.summary}
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <DetailCard label="Year" value={achievement.year} />
                <DetailCard label="Position" value={achievement.position} />
                <DetailCard label="Date" value={achievement.date} />
                <DetailCard label="Location" value={achievement.location} />
              </div>
            </div>
            <div className="aspect-[16/9] w-full overflow-hidden bg-surface-alt">
              <img
                src={achievement.image}
                alt={achievement.title}
                className="w-full h-full object-contain"
                loading="lazy"
              />
            </div>
          </article>

          <section className="bg-white border border-brand/15 rounded-xl p-5 sm:p-7 md:p-8 shadow-sm">
            <h3
              className="text-2xl text-brand font-semibold mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Student Participants
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left">
                <thead className="bg-brand text-surface">
                  <tr>
                    {participantHeadings.map((heading) => (
                      <th key={heading} className="px-4 py-3 font-semibold whitespace-nowrap">
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {achievement.students.map((student) => {
                    const visibleCells = hasParticipantAchievement
                      ? [student[0], student[1], student[4] || "-"]
                      : [student[0], student[1]];

                    return (
                      <tr key={student.join("-")} className="border-t border-brand/10">
                        {visibleCells.map((cell, cellIndex) => (
                          <td key={`${student[0]}-${cellIndex}`} className="px-4 py-3 align-top text-[#2a2a2a]">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <Link
            to="/campus-life/sports-gym/sports-achievement"
            className="inline-flex items-center gap-2 text-brand underline text-sm font-semibold"
          >
            <ArrowLeft size={14} /> Back to Sports Achievements
          </Link>
        </div>
      </section>
    </main>
  );
};

const updateMeta = (name, content) => {
  if (!content) return;

  const selector = `meta[name='${name}'], meta[property='${name}']`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    if (name.startsWith("og:")) {
      element.setAttribute("property", name);
    } else {
      element.setAttribute("name", name);
    }
    document.head.appendChild(element);
  }

  element.setAttribute("content", content);
};

const DetailCard = ({ label, value }) => (
  <div className="rounded-lg bg-surface-alt border border-brand/10 p-4">
    <p className="text-xs uppercase tracking-wider text-[#3a3a3a]/65 font-sans-ui">{label}</p>
    <p className="mt-2 text-base text-[#2a2a2a] font-semibold">{value}</p>
  </div>
);

export default SportsAchievementDetail;
